import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Funnel, ArrowClockwise, WarningCircle, TrendUp, CheckCircle, XCircle, Clock, Fire } from "@phosphor-icons/react";

const fmtINR = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const bucketColor = (key) => ({
  "0-7":   "bg-emerald-500",
  "8-14":  "bg-teal-500",
  "15-30": "bg-blue-500",
  "31-60": "bg-amber-500",
  "61-90": "bg-orange-500",
  "90+":   "bg-red-500",
}[key] || "bg-slate-400");

export default function PipelineSummaryPage() {
  const [status, setStatus] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // stage_id → bool
  const [showStaleOnly, setShowStaleOnly] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/integrations/hubspot");
      setStatus(data);
      return data?.configured;
    } catch (e) {
      setStatus({ configured: false });
      return false;
    }
  };

  const loadPipelines = async () => {
    try {
      const { data } = await api.get("/crm/pipelines");
      setPipelines(data?.pipelines || []);
    } catch (e) { /* silent */ }
  };

  const loadSummary = async (pid = "") => {
    setBusy(true); setError(null);
    try {
      const url = pid ? `/crm/pipeline-summary?pipeline_id=${encodeURIComponent(pid)}` : "/crm/pipeline-summary";
      const { data } = await api.get(url);
      setSummary(data);
      if (data?.pipeline_id) setPipelineId(data.pipeline_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Failed to load pipeline summary");
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    (async () => {
      await loadStatus();
      // Pipeline summary and pipelines list are always sourced from the
      // internal CRM snapshot — even without HubSpot connected, so load them
      // unconditionally.
      await loadPipelines();
      await loadSummary();
    })();
  }, []);

  const maxStageValue = useMemo(() => {
    if (!summary?.stages) return 1;
    return Math.max(1, ...summary.stages.filter(s => !s.is_closed).map(s => s.total_amount || 0));
  }, [summary]);

  const maxBucketCount = useMemo(() => {
    if (!summary?.aging_summary) return 1;
    return Math.max(1, ...summary.aging_summary.map(b => b.count));
  }, [summary]);

  const threshold = summary?.stale_threshold_days || 30;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="pipeline-summary-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">CRM · Live from your internal CRM</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Pipeline summary</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Live pipeline value by stage plus aging analysis. Deals in the same stage for more than <b>{threshold} days</b> are flagged as stale so you know exactly where to push.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {pipelines.length > 1 && (
            <select value={pipelineId} onChange={(e) => { setPipelineId(e.target.value); loadSummary(e.target.value); }}
                    disabled={busy} data-testid="pipeline-selector"
                    className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
              {pipelines.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          )}
          <button onClick={() => loadSummary(pipelineId)} disabled={busy} data-testid="pipeline-refresh"
                  className="text-sm inline-flex items-center gap-2 border border-slate-300 rounded-sm px-3 py-2 hover:bg-slate-100 disabled:opacity-50">
            <ArrowClockwise size={14} className={busy ? "animate-spin" : ""}/>{busy ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 border-l-4 border-red-500 bg-red-50 p-3 rounded-sm text-sm text-red-800" data-testid="pipeline-error">{error}</div>
      )}

      {busy && !summary && (
        <div className="mt-8 text-sm text-slate-500">Loading pipeline data…</div>
      )}

      {summary && (
        <>
          {/* Top KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="pipeline-kpis">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center gap-2 label-micro"><TrendUp size={14} className="text-blue-600"/>Open pipeline</div>
              <div className="font-display text-3xl font-black mt-2 text-slate-900" data-testid="kpi-open-value">{fmtINR(summary.total_open_value)}</div>
              <div className="text-xs text-slate-500 mt-1">{summary.total_open_count} deal{summary.total_open_count === 1 ? "" : "s"} · {summary.pipeline_label || "Default"}</div>
            </div>
            <button onClick={() => setShowStaleOnly(!showStaleOnly)} data-testid="kpi-stale-toggle"
                    className={`text-left rounded-sm p-5 border transition-colors ${showStaleOnly ? "bg-red-50 border-red-500 ring-2 ring-red-200" : "bg-white border-slate-200 hover:border-red-400"}`}>
              <div className="flex items-center gap-2 label-micro"><Fire size={14} className="text-red-500"/>Stale (&gt; {threshold} days)</div>
              <div className="font-display text-3xl font-black mt-2 text-red-600" data-testid="kpi-stale-value">{fmtINR(summary.total_stale_value)}</div>
              <div className="text-xs text-red-700 mt-1">{summary.total_stale_count} deal{summary.total_stale_count === 1 ? "" : "s"} · tap to {showStaleOnly ? "clear" : "filter"}</div>
            </button>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center gap-2 label-micro"><CheckCircle size={14} className="text-emerald-600"/>Closed won</div>
              <div className="font-display text-3xl font-black mt-2 text-emerald-700" data-testid="kpi-won-value">{fmtINR(summary.closed_won_value)}</div>
              <div className="text-xs text-slate-500 mt-1">All-time</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center gap-2 label-micro"><XCircle size={14} className="text-red-500"/>Closed lost</div>
              <div className="font-display text-3xl font-black mt-2 text-red-600" data-testid="kpi-lost-value">{fmtINR(summary.closed_lost_value)}</div>
              <div className="text-xs text-slate-500 mt-1">All-time</div>
            </div>
          </div>

          {/* Aging summary */}
          {summary.aging_summary && (
            <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="aging-summary">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-slate-600"/>
                <h2 className="font-display font-bold">Aging summary</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">Distribution of open deals by how long they've been in their current stage.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {summary.aging_summary.map((b) => {
                  const pct = Math.round((b.count / maxBucketCount) * 100);
                  const isOverdue = b.key === "31-60" || b.key === "61-90" || b.key === "90+";
                  return (
                    <div key={b.key} className={`border rounded-sm p-3 ${isOverdue ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-slate-50/50"}`} data-testid={`aging-bucket-${b.key}`}>
                      <div className="flex items-center justify-between">
                        <span className="label-micro">{b.label}</span>
                        {isOverdue && <Fire size={12} className="text-red-500"/>}
                      </div>
                      <div className="font-display text-2xl font-black mt-1 text-slate-900">{b.count}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{fmtINR(b.value)}</div>
                      <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full ${bucketColor(b.key)}`} style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stale deal alerts */}
          {summary.stale_deals && summary.stale_deals.length > 0 && (
            <div className="mt-6 bg-white border border-slate-200 rounded-sm" data-testid="stale-deals-panel">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <Fire size={16} className="text-red-500"/>
                <h2 className="font-display font-bold">Stale deal alerts</h2>
                <span className="ml-2 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold">{summary.stale_deals.length}{summary.stale_deals.length === 50 ? "+" : ""}</span>
                <span className="ml-auto text-xs text-slate-500">In current stage &gt; {threshold} days</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {summary.stale_deals.map((d) => (
                  <div key={d.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`stale-deal-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Stuck in <b className="text-slate-700">{d.stage_label}</b></div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs font-bold px-2 py-1 rounded-sm ${d.days_in_stage > 90 ? "bg-red-100 text-red-700" : d.days_in_stage > 60 ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>{d.days_in_stage}d</span>
                      <span className="font-display font-black text-slate-900 min-w-[90px] text-right">{fmtINR(d.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stage breakdown */}
          <div className="mt-6 bg-white border border-slate-200 rounded-sm" data-testid="pipeline-stages">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <Funnel size={16} className="text-slate-600"/>
              <h2 className="font-display font-bold">Value by stage</h2>
              <span className="text-xs text-slate-500 ml-2">Scanned {summary.fetched_deal_count} deals</span>
              {showStaleOnly && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold">Stale filter on</span>}
            </div>
            {summary.stages.length === 0 && <div className="p-6 text-sm text-slate-500">No stages found for this pipeline.</div>}
            <ul className="divide-y divide-slate-100">
              {summary.stages.map((s) => {
                if (showStaleOnly && s.stale_count === 0) return null;
                const isOpen = !s.is_closed;
                const pct = isOpen ? Math.round(((s.total_amount || 0) / maxStageValue) * 100) : 0;
                const isExp = !!expanded[s.id];
                const barColor = s.is_closed ? (s.is_won ? "bg-emerald-500" : "bg-red-400") : "bg-blue-500";
                const dealsToShow = showStaleOnly ? s.deals.filter(d => d.is_stale) : s.deals;
                return (
                  <li key={s.id} className="p-4" data-testid={`stage-${s.id}`}>
                    <button onClick={() => setExpanded({ ...expanded, [s.id]: !isExp })}
                            className="w-full text-left"
                            data-testid={`stage-toggle-${s.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`inline-block w-2 h-2 rounded-full ${s.is_closed ? (s.is_won ? "bg-emerald-500" : "bg-red-400") : "bg-blue-500"}`}></span>
                          <span className="font-semibold text-sm text-slate-900 truncate">{s.label}</span>
                          {s.is_closed && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.is_won ? "closed won" : "closed lost"}</span>}
                          {isOpen && s.stale_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full" data-testid={`stage-stale-badge-${s.id}`}>
                              <Fire size={9} weight="fill"/>{s.stale_count} stale · {fmtINR(s.stale_value)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-500">{s.count} deal{s.count === 1 ? "" : "s"}</span>
                          <span className="font-display font-black text-slate-900 min-w-[110px] text-right" data-testid={`stage-value-${s.id}`}>{fmtINR(s.total_amount)}</span>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${isOpen ? pct : 100}%` }}/>
                      </div>
                    </button>
                    {isExp && dealsToShow.length > 0 && (
                      <ul className="mt-3 space-y-1 pl-4 border-l-2 border-slate-100" data-testid={`stage-deals-${s.id}`}>
                        {dealsToShow.map(d => (
                          <li key={d.id} className="flex items-center justify-between gap-2 text-xs py-1">
                            <span className={`truncate flex-1 ${d.is_stale ? "text-red-700 font-semibold" : "text-slate-700"}`}>{d.name}</span>
                            <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${d.is_stale ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{d.days_in_stage}d</span>
                            <span className="text-slate-600 shrink-0 font-semibold min-w-[70px] text-right">{fmtINR(d.amount)}</span>
                          </li>
                        ))}
                        {!showStaleOnly && s.count > s.deals.length && (
                          <li className="text-[11px] text-slate-500 italic pt-1">+ {s.count - s.deals.length} more…</li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
